/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import Icon, { IconNames } from 'components/icons'
import { PageBlock } from 'components/layout'
import SearchBox from 'components/search-box'
import theme from 'components/theme'
import { TRIP_TYPES } from 'constants/tripTypes'
import { getDayJsObjectFromLocal } from 'helpers/dates'
import { isEmpty, isMobileOrTablet } from 'helpers/utils'
import NAMESPACES from 'localization/constants/namespaces'
import { getCabins } from 'selectors/cabins'
import { mapMediaType } from 'selectors/mediaType'
import { getSuggestionsState } from 'selectors/suggestions'

import {
  Content,
  ErrorText,
  ErrorTextContainer,
  ErrorTitle,
  SearchboxWrapper,
  UnavailableFlightDateErrorContainer,
  Wrapper
} from './styled'
import { IconContainer } from '../icons/styled'

const ICON_SIZE = 22

export const ReSearchBoxErrorMessage = ({ flightType }) => {
  const { t } = useTranslation(NAMESPACES.FLIGHT_OFFERS)

  return (
    <UnavailableFlightDateErrorContainer>
      <IconContainer height={ICON_SIZE} minWidth={ICON_SIZE}>
        <Icon name={IconNames.Info} size={ICON_SIZE} color={theme.colors.doveGrayer} />
      </IconContainer>
      <ErrorTextContainer>
        <ErrorTitle
          dangerouslySetInnerHTML={{
            __html:
              flightType === TRIP_TYPES.ONE_WAY
                ? t('shopping.re-search-box.unavailable-flight')
                : t('shopping.re-search-box.unavailable-flight.other')
          }}
        />
        <ErrorText
          style={{ 'line-height': '30px' }}
          dangerouslySetInnerHTML={{
            __html: t('shopping.re-search-box.make-new-search')
          }}
        />
      </ErrorTextContainer>
    </UnavailableFlightDateErrorContainer>
  )
}

const ReSearchBoxCollapsible = props => {
  const [originalSuggestions, setOriginalSuggestions] = useState([])
  const [errorMessage, clearErrorMessage] = useState(false)

  // Save original suggestions given that on each fetch
  // one's legs suggestion get deleted
  useEffect(() => {
    setOriginalSuggestions(props.suggestions)
  }, [props.suggestions])

  const getSuggestionByIATACode = (suggestions, code) =>
    (suggestions || []).find(
      sugg => sugg && sugg.id && sugg.id.toLowerCase() === code.toLowerCase()
    )

  const findSuggestions = (suggestionField, field) => {
    let result = getSuggestionByIATACode(props.suggestions[suggestionField][0], field)

    if (isEmpty(result) && !isEmpty(originalSuggestions)) {
      result = getSuggestionByIATACode(originalSuggestions[suggestionField][0], field)
    }

    return result
  }

  const findSuggestionsMulti = (suggestionField, field) => {
    let result = getSuggestionByIATACode(
      Object.keys(props.suggestions[suggestionField])
        .map(key => props.suggestions[suggestionField][key])
        .reduce((a, b) => a.concat(b), []),
      field
    )

    if (isEmpty(result) && !isEmpty(originalSuggestions)) {
      result = getSuggestionByIATACode(
        Object.keys(originalSuggestions[suggestionField])
          .map(key => originalSuggestions[suggestionField][key])
          .reduce((a, b) => a.concat(b), []),
        field
      )
    }

    return result
  }

  const getLegsWithObjectSuggestion = () => {
    const {
      flightType,
      flightsSearchData: { legs }
    } = props

    if (flightType === TRIP_TYPES.ROUND_TRIP && legs && legs.length > 1) {
      return [
        {
          fromSelected: findSuggestions('suggestionsFrom', legs[0].origin),
          toSelected: findSuggestions('suggestionsTo', legs[0].destination),
          date: {
            from: getDayJsObjectFromLocal(legs[0].departure).toDate(),
            to: getDayJsObjectFromLocal(legs[1].departure).toDate()
          },
          isExpanded: true
        }
      ]
    }

    return (legs || []).map(item => {
      return {
        fromSelected: findSuggestionsMulti('suggestionsFrom', item.origin),
        toSelected: findSuggestionsMulti('suggestionsTo', item.destination),
        date: { from: getDayJsObjectFromLocal(item.departure).toDate() },
        isExpanded: true
      }
    })
  }

  const onChangeFlightType = () => clearErrorMessage(true)

  const {
    cabins,
    suggestions,
    flightsSearchData,
    isFetching,
    mediaType,
    flightType,
    isCampaignRestricted,
    campaignProps,
    campaignFieldsInitialValues,
    noResults,
    isExpanded,
    id
  } = props

  const { suggestionsFrom, suggestionsTo } = suggestions

  if (!suggestionsFrom || !suggestionsTo || isEmpty(cabins) || isFetching) {
    return null
  }

  const { adt, inf, chd, flexDates, cabinClass, promoCode, awardBooking } = flightsSearchData
  const isMobileOrTabletDevice = isMobileOrTablet(mediaType)

  return (
    <Wrapper>
      {noResults && isMobileOrTabletDevice && !errorMessage && (
        <ReSearchBoxErrorMessage flightType={flightType} />
      )}
      <Content id={id} aria-hidden={!isExpanded} isExpanded={isExpanded}>
        <SearchboxWrapper isExpanded={isExpanded}>
          <PageBlock>
            <SearchBox
              noResults={noResults}
              isResearch
              flexDates={flexDates === 'false' ? false : true}
              flightType={flightType}
              legs={getLegsWithObjectSuggestion()}
              passengers={{
                passengers: [parseInt(adt, 10), parseInt(chd, 10), parseInt(inf, 10)],
                radioGroupValue: cabinClass
              }}
              isResearchBoxExpanded={isExpanded}
              onChangeFlightType={onChangeFlightType}
              isCampaignRestricted={isCampaignRestricted}
              campaignFieldsInitialValues={campaignFieldsInitialValues}
              campaignProps={campaignProps}
              promoCode={promoCode}
              awardBooking={awardBooking}
            />
          </PageBlock>
        </SearchboxWrapper>
      </Content>
    </Wrapper>
  )
}

ReSearchBoxCollapsible.propTypes = {
  flightsSearchData: PropTypes.object,
  isFetching: PropTypes.bool,
  flightType: PropTypes.oneOf(Object.values(TRIP_TYPES)),
  isCampaignRestricted: PropTypes.bool,
  campaignProps: PropTypes.object,
  campaignFieldsInitialValues: PropTypes.object,
  cabins: PropTypes.array,
  suggestions: PropTypes.object,
  mediaType: PropTypes.string,
  noResults: PropTypes.bool,
  isExpanded: PropTypes.bool
}

const mapStateToProps = state => ({
  cabins: getCabins(state),
  suggestions: getSuggestionsState(state),
  mediaType: mapMediaType(state)
})

export default connect(mapStateToProps)(ReSearchBoxCollapsible)
